import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { Driver } from '../types/driver';

export type ActiveDriver = Pick<Driver, 'uuid' | 'name'> & { phone_number: string | null };

export interface ActiveDriversSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: ActiveDriver[];
    links: { self: string };
}

export const useGetActiveDrivers = () => {
    const config = useApiConfig();
    const { t } = useTranslation();
    const configRef = useRef({ ...config, t });
    configRef.current = { ...config, t };
    const requestId = useRef(0);
    const [drivers, setDrivers] = useState<ActiveDriver[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const request = useCallback(async () => {
        const id = ++requestId.current;
        const { getHeaders, getCommonParameters, handleCommonErrors, t } = configRef.current;
        setLoading(true);
        setError(null);
        try {
            const parameters = new URLSearchParams(getCommonParameters());
            const response = await fetch(`${API_URL}/users/get-active-drivers?${parameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });
            if (!response.ok) {
                const json = await response.json().catch(() => ({}));
                const apiError = new Error(json.message || t('common.error-occured')) as ApiError;
                apiError.statusCode = response.status;
                apiError.errors = json.errors;
                throw apiError;
            }
            const data: ActiveDriversSuccessResponse = await response.json();
            if (id === requestId.current) setDrivers(data.data);
        } catch (err) {
            if (id !== requestId.current) return;
            const apiError = err as ApiError;
            handleCommonErrors(apiError);
            setError(apiError.message || t('common.error-occured'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, []);

    return { drivers, request, loading, error };
};
